// Types for ICD10 extraction system
export interface ICD10Code {
  code: string;
  description?: string;
  explanation?: string;
  confidence?: number;
  text_references?: TextReferenceModel[];
}

export interface TextReferenceModel {
  text_snippet?: string;
  start?: number;
  end?: number;
  text?: string;
  codes?: ICD10Code[];
}

export enum ExtractionStep {
  EXTRACTION_STARTED = 'extraction_started',
  CONTEXT_BUILDING = 'context_building',
  TEXT_PRUNING = 'text_pruning',
  TEXT_SUMMARIZATION = 'text_summarization',
  CONTEXT_COMPLETED = 'context_completed',
  QUERY_GENERATION = 'query_generation',
  QUERY_COMPLETED = 'query_completed',
  ICD10_SEARCH = 'icd10_search',
  EXPANSION = 'expansion',
  MINIMIZATION = 'minimization',
  DEDUPLICATION = 'deduplication',
  SEARCH_COMPLETED = 'search_completed',
  CODE_SELECTION = 'code_selection',
  SELECTION_COMPLETED = 'selection_completed',
  MAIN_CODE_SELECTION = 'main_code_selection',
  MAIN_CODE_SELECTED = 'main_code_selected',
  FINAL_PROCESSING = 'final_processing',
  EXTRACTION_COMPLETED = 'extraction_completed',
}

export interface ExtractionProgressData {
  request_id: string;
  stage: ExtractionStep | 'pcs_started' | 'pcs_completed';
  message: string;
  progress_percent: number;
  timestamp: string;
  data?: {
    uncertain_words?: string[];
    step_duration?: number;
    codes_found?: number;
    queries_generated?: number;
    [key: string]: any;
  };
}

export interface ExtractionRequest {
  text: string;
  agent_preset?: string;
  max_queries?: number;
  k_per_query?: number;
  max_codes?: number;
  select_main_code?: boolean;
  use_rag?: boolean;
  temperature?: number;
  streaming_callback?: boolean;
  feedback?: string;
  run_diagnostic?: boolean;
  run_pcs?: boolean;
  pcs_top_k?: number;
  pcs_include_refinement?: boolean;
  locale?: string;
}

export interface ExtractionResponse {
  request_id?: string;
  codes: ICD10Code[];
  processing_time: number;
  timestamp: string;
  text_references?: TextReferenceModel[];
  main_code_selected?: boolean;
  metadata?: {
    processing_time?: number;
    total_codes_found?: number;
    query_count?: number;
    confidence_score?: number;
    summarized?: boolean;
    summarized_text?: string | null;
    pruned_text?: string | null;
    main_code_selection?: {
      selected_code?: string;
      rationale?: string;
      error?: string;
      candidates?: string[];
    };
    [key: string]: any;
  };
  costs?: Record<string, any>;
  pcs?: PcsExtractionResponse | null;
  ran_diagnostic?: boolean;
  ran_pcs?: boolean;
}

export interface PcsOption {
  code: string;
  label_es: string;
  label_en: string;
}

export interface PcsCandidate {
  score: number;
  procedure_code: string;
  procedure_label: Record<string, string>;
  localization_code: string;
  localization_label: Record<string, string>;
  code_prefix: string;
  options: Record<string, PcsOption[]>;
  metadata: Record<string, any>;
}

export interface PcsRagCandidate {
  code4: string;
  representative_code7: string;
  desc: string;
  score_components: Record<string, number>;
  score_total: number;
  why: string;
}

export interface PcsRagResult {
  query_id: string;
  top_k: number;
  candidates: PcsRagCandidate[];
}

export interface PcsChoice {
  query_id: string;
  selected_code4: string;
  confidence: number;
}

export interface PcsAmbiguous {
  query_id: string;
  reason: string;
  suggested_clarifications: string[];
}

export interface PcsFinalCode {
  query_id: string;
  code4: string;
  code7: string;
  approach: string;
  device: string;
  qualifier: string;
  rationale: string;
  confidence?: number | null;
  text_references?: Array<{ text_snippet: string }>;
  source?: string;
}

export interface PcsRefinement {
  needs_clarification: boolean;
  selections: Record<string, any>[];
  reasoning: string;
  raw_response?: string | null;
  parser_error?: string | null;
  ambiguous: Record<string, any>[];
}

export interface PcsExtractionResponse {
  query: string;
  language: string;
  candidates: PcsCandidate[];
  per_query_candidates: Record<string, PcsCandidate[]>;
  refinement?: PcsRefinement | null;
  query_plan: Record<string, any>;
  rag_results: PcsRagResult[];
  choices: PcsChoice[];
  ambiguous_or_unmatched: PcsAmbiguous[];
  final_codes: PcsFinalCode[];
  context_summary: string;
  uncertain_words: string[];
  queries: string[];
  processing_time: number;
  timestamp: string;
  request_id?: string;
  costs?: Record<string, any> | null;
}

// Shape returned by GET /extractions and GET /extraction/{request_id}
export interface ExtractionResult {
  user_id?: string;
  request_id: string;
  input_text?: string;
  extraction_json?: Record<string, any> | string | null; // raw JSON payload or string
  user_feedback?: string;
  codes?: ICD10Code[]; // parsed client-side from extraction_json when available
  created_at?: number | null;
  duration_seconds?: number | null;
  extra_json?: Record<string, any> | null;
  pcs?: PcsExtractionResponse | null;
}

export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  ERROR = 'error'
}

export type CodeSystem = 'diagnosis' | 'pcs';

export interface CodeMetadataItem {
  code: string;
  system?: CodeSystem;
  locale?: string;
}

export interface CodeMetadataRequest {
  codes: CodeMetadataItem[];
  locale?: string;
  system?: CodeSystem;
}

export interface CodeMetadataResult {
  code: string;
  system: CodeSystem;
  found: boolean;
  locale_used?: string | null;
  data?: Record<string, any> | null;
  error?: string | null;
}

export interface CodeMetadataResponse {
  results: CodeMetadataResult[];
  processing_time: number;
  timestamp: string;
  request_id?: string;
}

export interface AgentPreset {
  key: string;
  // Technical configuration from backend
  relative_cost?: string;
  requires_providers?: string[];
  primary_provider?: string;
  models?: {
    complex_reasoning?: string;
    simple_tasks?: string;
  };
  // Runtime metadata
  available?: boolean;
  unavailable_reason?: string;
}

export interface AgentPresetsResponse {
  presets: Record<string, AgentPreset>;
  timestamp: string;
}

export interface SocketError {
  message: string;
  code?: string;
  timestamp: string;
}
